/**
 * Content filtering utilities for processing AI-generated content
 */

/**
 * Removes <think>...</think> tags and their content from the input string
 * Handles both single-line and multi-line think tags
 *
 * @param content - The content string to filter
 * @returns The filtered content with think tags removed
 *
 * @example
 * removeThinkTags("<think>internal reasoning</think>Here is the answer")
 * // Returns: "Here is the answer"
 *
 * @example
 * removeThinkTags("Some text\n<think>\nmulti-line\nthinking\n</think>\nMore text")
 * // Returns: "Some text\nMore text"
 */
export function removeThinkTags(content: string): string {
  if (!content) return content;

  // Remove <think>...</think> tags and their content
  // This regex handles both single-line and multi-line cases
  // The 's' flag makes . match newlines as well
  const filtered = content.replace(/<think>[\s\S]*?<\/think>/gi, '');

  // Clean up any excessive whitespace that might be left behind
  // Replace multiple consecutive newlines with at most two newlines
  return filtered.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Checks if content contains think tags
 *
 * @param content - The content string to check
 * @returns True if think tags are present, false otherwise
 */
export function hasThinkTags(content: string): boolean {
  if (!content) return false;
  return /<think>[\s\S]*?<\/think>/gi.test(content);
}

/**
 * Extracts only the content within think tags (useful for debugging)
 *
 * @param content - The content string to extract from
 * @returns Array of strings containing the content within think tags
 */
export function extractThinkContent(content: string): string[] {
  if (!content) return [];

  const matches = content.match(/<think>([\s\S]*?)<\/think>/gi);
  if (!matches) return [];

  return matches.map(match =>
    match.replace(/<\/?think>/gi, '').trim()
  );
}
